// Agency engagements (ADR-0015).
//
// One authorisation from a client to an agency, and the grants that hang off
// it. Every function here is a permission-checked RPC for the same reason the
// People ones are: the rules an engagement enforces — the role must be one the
// client allowed, the account must be one the client named, the expiry cannot
// outlast the authorisation — are not things a row policy can express.
//
// Note which side each call is authorised from. Staffing is the agency's;
// `authorise` and `renew` are the client's alone (an agency that could renew its
// own authorisation is an agency that never loses access); `revoke` and
// `unassign` are either side's, because a client must always be able to end it
// and an agency must always be able to walk away.
//
// ADR-0021 moved the agency from a workspace to an ORGANISATION. Everything here
// that used to take an agency workspace id now takes an organisation id — see
// `lib/organisation.ts` for the roster, teams and seats that hang off it.

import { supabase } from "./supabase";
import { asAppError } from "./entitlements";
import type { Platform } from "./types";
// Offboarding and Remove now report the same shape: what went, and what stayed.
import type { RemovalOutcome } from "./people";

export type EngagementScope = "workspace" | "connections";

/** `expired` is computed, not stored: an engagement past its date reads as
 *  expired everywhere before anything sweeps the row. */
export type EngagementStatus = "active" | "expired" | "revoked";

export interface EngagementAccount {
  id: string;
  name: string;
  platform: Platform;
}

export interface EngagementPerson {
  user_id: string;
  email: string | null;
  role: string;
  scope_id: string | null;
  expires_at: string | null;
}

/** One client, seen from the agency. */
export interface Engagement {
  id: string;
  client_workspace_id: string;
  client_name: string;
  status: EngagementStatus;
  allowed_roles: string[];
  scope: EngagementScope;
  expires_at: string | null;
  note: string | null;
  created_at: string;
  /** Whether *this* viewer may staff it. Admins may staff every client; a
   *  manager only the ones they are named on. */
  may_staff: boolean;
  accounts: EngagementAccount[];
  people: EngagementPerson[];
  teams: EngagementTeam[];
}

export interface EngagementTeam {
  team_id: string;
  name: string;
  role: string;
  members: number;
}

export interface AgencyStaff {
  user_id: string;
  email: string | null;
  org_role: "owner" | "admin" | "manager" | "member";
  is_owner: boolean;
  teams: string[];
  /** How many clients would lose this person if they left the organisation. */
  clients: number;
}

/** One agency, seen from the client. */
export interface ClientEngagement {
  id: string;
  agency_org_id: string;
  agency_name: string;
  agency_handle: string | null;
  status: EngagementStatus;
  allowed_roles: string[];
  scope: EngagementScope;
  expires_at: string | null;
  created_at: string;
  accounts: string[];
  people: string[];
}

export async function agencyPortfolio(organisationId: string): Promise<Engagement[]> {
  const { data, error } = await supabase.rpc("agency_portfolio", {
    p_organisation_id: organisationId,
  });
  if (error) throw asAppError(error);
  return (data ?? []) as Engagement[];
}

export async function agencyStaff(organisationId: string): Promise<AgencyStaff[]> {
  const { data, error } = await supabase.rpc("agency_staff", {
    p_organisation_id: organisationId,
  });
  if (error) throw asAppError(error);
  return (data ?? []) as AgencyStaff[];
}

export async function clientEngagements(clientWorkspaceId: string): Promise<ClientEngagement[]> {
  const { data, error } = await supabase.rpc("client_engagements", {
    p_client_workspace_id: clientWorkspaceId,
  });
  if (error) throw asAppError(error);
  return (data ?? []) as ClientEngagement[];
}

/** Exact-handle lookup, the way a username is looked up. Null when there is no
 *  such agency — enough to confirm who you are authorising, not enough to
 *  enumerate anyone. */
export async function findAgency(handle: string): Promise<
  { organisation_id: string; name: string; handle: string } | null
> {
  const { data, error } = await supabase.rpc("find_agency", { p_handle: handle });
  if (error) throw asAppError(error);
  return (data ?? null) as { organisation_id: string; name: string; handle: string } | null;
}

/** The client's call, and only the client's: this is what sets the envelope. */
export async function authoriseAgency(args: {
  clientWorkspaceId: string;
  handle: string;
  allowedRoles: string[];
  scope: EngagementScope;
  connectionIds?: string[];
  expiresAt?: string | null;
  note?: string | null;
}): Promise<Engagement> {
  const { data, error } = await supabase.rpc("authorise_agency", {
    p_client_workspace_id: args.clientWorkspaceId,
    p_agency_handle: args.handle,
    p_allowed_roles: args.allowedRoles,
    p_scope: args.scope,
    p_connection_ids: args.connectionIds ?? [],
    p_expires_at: args.expiresAt ?? null,
    p_note: args.note ?? null,
  });
  if (error) throw asAppError(error);
  return data as Engagement;
}

/**
 * Ends the engagement and every grant made under it, in one transaction.
 *
 * This is the honest version of the single Revoke button: not a loop over N
 * people that can half-fail, but one statement in the database.
 */
export async function revokeEngagement(engagementId: string): Promise<void> {
  const { error } = await supabase.rpc("revoke_engagement", { p_engagement_id: engagementId });
  if (error) throw asAppError(error);
}

/**
 * Extend an engagement in place, keeping its id and therefore every grant
 * threaded through it. The client's call alone.
 *
 * The alternative — revoke and re-authorise — destroys the history and makes the
 * agency re-staff every client by hand.
 */
export async function renewEngagement(
  engagementId: string, expiresAt: string | null,
): Promise<Engagement> {
  const { data, error } = await supabase.rpc("renew_engagement", {
    p_engagement_id: engagementId, p_expires_at: expiresAt,
  });
  if (error) throw asAppError(error);
  return data as Engagement;
}

export async function assignAgencyMember(args: {
  engagementId: string;
  userId: string;
  role: string;
  connectionId?: string | null;
  expiresAt?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc("assign_agency_member", {
    p_engagement_id: args.engagementId,
    p_user_id: args.userId,
    p_role_code: args.role,
    p_connection_id: args.connectionId ?? null,
    p_expires_at: args.expiresAt ?? null,
  });
  if (error) throw asAppError(error);
}

/** A whole pod on a client, in one grant instead of one per person. */
export async function assignAgencyTeam(args: {
  engagementId: string;
  teamId: string;
  role: string;
  connectionId?: string | null;
  expiresAt?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc("assign_agency_team", {
    p_engagement_id: args.engagementId,
    p_team_id: args.teamId,
    p_role_code: args.role,
    p_connection_id: args.connectionId ?? null,
    p_expires_at: args.expiresAt ?? null,
  });
  if (error) throw asAppError(error);
}

export async function unassignAgencyMember(engagementId: string, userId: string): Promise<void> {
  const { error } = await supabase.rpc("unassign_agency_member", {
    p_engagement_id: engagementId, p_user_id: userId,
  });
  if (error) throw asAppError(error);
}

/**
 * Leaving the organisation: the one door out.
 *
 * Ends a departing employee's access at every client, in one transaction, and
 * takes them out of every pod on the way — a team grant would otherwise carry
 * them straight back in through the resolver.
 *
 * `retained` is what it deliberately did NOT end: a grant a client made
 * directly is theirs, not the agency's, and reporting "removed" over the top of
 * one is the failure this return value exists to prevent (ADR-0021 L-C).
 */
export async function removeOrganisationMember(args: {
  organisationId: string;
  userId: string;
}): Promise<RemovalOutcome> {
  const { data, error } = await supabase.rpc("remove_organisation_member", {
    p_organisation_id: args.organisationId,
    p_user_id: args.userId,
  });
  if (error) throw asAppError(error);
  return data as RemovalOutcome;
}
